import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import Header from "../../components/header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import CtaSection from "../../components/CtaSection/CtaSection";

import { useSeo } from "../../hooks/useSeo";

const staticMeta = {
  title: "Case Studies | RashTech Info",
  description:
    "Read RashTech Info case studies showcasing real-world solutions in web, mobile, and software development across industries.",
  url: "https://rashtechinfo.com/case-study",
  image: "https://rashtechinfo.com/og-image.png", // replace with your real asset if available
  siteName: "RashTech Info",
  keywords:
    "case studies, success stories, projects, web development, mobile apps, software, RashTech Info",
  themeColor: "#0A0D1C",
};

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: staticMeta.siteName,
  url: "https://rashtechinfo.com/",
  logo: "https://rashtechinfo.com/logo.png",
  sameAs: [
    "https://www.facebook.com/rashtechinfo",
    "https://www.linkedin.com/company/rashtechinfo",
    "https://twitter.com/rashtechinfo",
  ],
};

const CaseStudy = () => {
  const [caseStudies, setCaseStudies] = useState([]);

  // Load SEO entries from your API and select the one for the current route
  const { seo, loading: seoLoading, error: seoError } = useSeo("/getSeo");

  // Merge dynamic SEO over static defaults
  const computed = useMemo(() => {
    const dynamic = {
      title: seo?.title,
      description: seo?.metaDescription,
      keywords: seo?.metaKeywords,
      canonicalUrl: seo?.canonicalUrl,
    };
    return {
      title: dynamic.title || staticMeta.title,
      description: dynamic.description || staticMeta.description,
      keywords: dynamic.keywords || staticMeta.keywords,
      url: dynamic.canonicalUrl || staticMeta.url,
      image: staticMeta.image,
      siteName: staticMeta.siteName,
      themeColor: staticMeta.themeColor,
    };
  }, [seo]);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const res = await fetch(`https://api.rashtechinfo.com/casestudy`);
        if (!res.ok) {
          throw new Error("Failed to fetch data");
        }
        const data = await res.json();
        setCaseStudies(data.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchCaseStudies();
  }, []);

  const ClickHandler = () => {
    window.scrollTo(10, 0);
  };

  return (
    <Fragment>
      <Helmet>
        {/* Primary */}
        <title>{computed.title}</title>
        <meta name="description" content={computed.description} />
        {computed.keywords && (
          <meta name="keywords" content={computed.keywords} />
        )}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={computed.themeColor} />
        {computed.url && <link rel="canonical" href={computed.url} />}

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={computed.siteName} />
        <meta property="og:title" content={computed.title} />
        <meta property="og:description" content={computed.description} />
        {computed.url && <meta property="og:url" content={computed.url} />}
        {computed.image && <meta property="og:image" content={computed.image} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={computed.title} />
        <meta name="twitter:description" content={computed.description} />
        {computed.image && (
          <meta name="twitter:image" content={computed.image} />
        )}

        {/* Optional preconnects */}
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />

        {/* JSON-LD (Organization) */}
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

        {/* JSON-LD (Breadcrumbs for Case Studies) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://rashtechinfo.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Case Studies",
                item: computed.url,
              },
            ],
          })}
        </script>
      </Helmet>

      <Header />
      <main className="page_content about-page">
        <PageTitle
          pageTitle={"Our Case Studies"}
          pagesub={"Case Studies"}
          pageTop={"Our"}
        />

        <section className="portfolio_section section_space bg-light">
          <div className="container">
            <div className="filter_elements_wrapper row">
              {caseStudies.slice(0, 18).map((project, prj) => (
                <div className="col-lg-6" key={prj}>
                  <div className="portfolio_block portfolio_layout_2">
                    <div className="portfolio_image">
                      <Link
                        onClick={ClickHandler}
                        className="portfolio_image_wrap bg-light"
                        to={`/case-study/${project.caseStudyTitle.replace(/\s+/g, '-')}`}
                      >
                        <img
                          src={`https://api.rashtechinfo.com/uploads/${project.bannerImage}`}
                          alt={project.clientIndustry || "Case Study"}
                          style={{
                            width: "100%",
                            objectFit: "cover",
                            height: "100%",
                          }}
                          onError={(e) => {
                            const target = e.currentTarget;
                            if (target.dataset.fallbackApplied) return; // avoid loop
                            target.dataset.fallbackApplied = "1";
                            target.src =
                              "https://media.licdn.com/dms/image/v2/C5112AQEw1fXuabCTyQ/article-inline_image-shrink_1500_2232/article-inline_image-shrink_1500_2232/0/1581099611064?e=1761177600&v=beta&t=7hMw9_W0flONIBUwsc_H29TRe6QykU2h6zaf1rOHEzQ";
                          }}
                        />
                      </Link>
                    </div>
                    <div className="portfolio_content">
                      <p className="portfolio_title">
                        <Link
                          onClick={ClickHandler}
                          to={`/case-study/${project.caseStudyTitle.replace(/\s+/g, '-')}`}
                        >
                          {(project.projectBrief || "").length > 70
                            ? `${project.projectBrief.slice(0, 70)}...`
                            : project.projectBrief}
                        </Link>
                      </p>
                      <h3 className="portfolio_title">
                        <Link
                          onClick={ClickHandler}
                          to={`/case-study/${project.caseStudyTitle.replace(/\s+/g, '-')}`}
                        >
                          {project.caseStudyTitle}
                        </Link>
                      </h3>
                    </div>
                  </div>
                </div>
              ))}
              {caseStudies.length === 0 && (
                <div className="col-12 text-center py-4">
                  No case studies available at the moment.
                </div>
              )}
            </div>
          </div>
        </section>

        <CtaSection />
      </main>
      <Footer />
      <Scrollbar />

      {/* Optional hidden diagnostics */}
      {seoError && <div style={{ display: "none" }}>SEO load error</div>}
      {seoLoading && <div style={{ display: "none" }}>Loading SEO…</div>}
    </Fragment>
  );
};

export default CaseStudy;
